import type { HexColor } from "../types.ts";
import type { GenerationRatio, ResourceType } from "./Board/types.ts";
import type {
  BILLING_PERIODS,
  MODEL_TYPES,
  STYLE_MODEL_IMAGES_KIND,
} from "./constants.ts";

export type BillingPeriod = (typeof BILLING_PERIODS)[number];

export type BaseWorkspace = {
  uuid: string;
  name: string;
};

export type BaseUser = {
  uuid: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
};

export type User = BaseUser & {
  permissions: UserPermission[];
  workspaces: BaseWorkspace[];

  phone_number: string | null;

  occupation: string | null;
  industry: string | null;
  heard_about_us: string | null;
  annual_visual_content_budget: string | null;
  company_size: string | null;
  has_worked_recently_with_creative_agency: boolean | null;
  terms_and_conditions_agreement: boolean | null;

  onboarding_finished_at: string | null;
};

export type Member = BaseUser & {
  is_owner: boolean;
};

export type BrandType = "COLOR" | "FONT" | "LOGO";

type BaseBrand = {
  uuid: string;
  type: BrandType;
};

export type BrandColor = BaseBrand & {
  type: "COLOR";
  color: HexColor;
};

export type BrandFont = BaseBrand & {
  type: "FONT";
  family: string;
  font_url: string;
  is_builtin: boolean;
};

export type BrandLogo = BaseBrand & {
  type: "LOGO";
  thumbnail_url: string;
  content_uuid: string;
};

export type Brand = BrandColor | BrandFont | BrandLogo;

export type WorkspaceType = "basic" | "pro" | "premium" | "team" | "enterprise";

export type Workspace = BaseWorkspace & {
  type: WorkspaceType;
  users: Member[];
  permissions: UserWorkspaceSpecificPermission[];
  total_credits_remaining_amount: number;
  usage_based_additional_credits_remaining_amount: number;
  subscription_credits_next_added_at: string | null;
  brands: Brand[];
};

export type Credit = {
  valid_from: string;
  valid_until: string;
  remaining_amount: number;
  original_amount: number;
  origin:
    | "SUBSCRIPTION"
    | "TOP_UP"
    | "MANUAL"
    | "INCLUDED_WITH_FIRST_WORKSPACE";
};

export type WorkspaceDetails = {
  num_members: number;
  num_seats: number;
  credits: Credit[];
  additional_credits_consumed: number | null;
  additional_credits_limit: number;
  billing_period: BillingPeriod | null;
};

export type UserPermission =
  | "mode:debug"
  | "backoffice:edit"
  | "contents:curate"
  | "reference-tags:create"
  | "references:add-tags"
  | "references:copy"
  | "references:curate"
  | "references:delete-any"
  | "references:remove-tags"
  | "references:update-curated-references"
  | "credits:bypass-consumption";

export type UserWorkspaceSpecificPermission =
  | "subscription:upgrade:hidden"
  | "subscription:view"
  | "references:generate-with-custom-references";

export type ModelStatus = "CREATED" | "TRAINING" | "READY" | "ERROR";

export type StyleModelImagesKind = (typeof STYLE_MODEL_IMAGES_KIND)[number];

export type ModelType = (typeof MODEL_TYPES)[number];

export type ModelImage = {
  url: string;
  uuid: string;
};

export type ObjectDetails = {
  labels: string[] | null;
  materials: string[] | null;
};

export type CharacterDetails = {
  gender: string[] | null;
  age: string[] | null;
  hair: string[] | null;
  clothing: string[] | null;
};

export type PhotographyColorDetails = {
  palette: string[] | null;
};

export type IllustrationColorDetails = {
  palette: string[] | null;
  texture: string[] | null;
};

export type PhotographyStyleDetails = {
  camera_type: string[] | null;
  composition: string[] | null;
  focus: string[] | null;
  exposure: string[] | null;
  image_quality: string[] | null;
};

export type IllustrationStyleDetails = {
  composition: string[] | null;
  level_of_detail: string[] | null;
  shape: string[] | null;
  stroke: string[] | null;
};

export type IconStyleDetails = {
  composition: string[] | null;
  level_of_detail: string[] | null;
  shape: string[] | null;
  stroke: string[] | null;
};

export type Graphic3DStyleDetails = {
  composition: string[] | null;
  level_of_detail: string[] | null;
  shape: string[] | null;
};

export type BaseModel = {
  uuid: string;
  name: string | null;
  status: ModelStatus;
  type: ModelType;
  thumbnail_url: string | null;
  is_curated: boolean;
  is_outdated: boolean;
  additional_elements: string | null;
  is_trained: boolean;
};

export type ModelWithIsCompatibleAndTags = BaseModel & {
  is_compatible: boolean;
  tags: string[];
};

type BaseModelWithContents = BaseModel & {
  contents: ModelImage[];
};

// FIXME: We should add a BaseElementModel type that would be the base of all the object and character models.

export type ObjectModel = BaseModelWithContents & {
  type: "OBJECT";
  details: ObjectDetails | null;
  element_description: string | null;
};

export type CharacterModel = BaseModelWithContents & {
  type: "CHARACTER";
  element_description: string | null;
  details: CharacterDetails | null;
};

type BaseStyleModel = BaseModelWithContents & {
  type: "STYLE";
  images_kind: StyleModelImagesKind | null;
};

export type PhotographyStyleModel = BaseStyleModel & {
  images_kind: "PHOTOGRAPHY";
  color_details: PhotographyColorDetails | null;
  details: PhotographyStyleDetails | null;
};

export type IllustrationStyleModel = BaseStyleModel & {
  images_kind: "ILLUSTRATION_2D";
  color_details: IllustrationColorDetails | null;
  details: IllustrationStyleDetails | null;
};

export type Graphic3DStyleModel = BaseStyleModel & {
  images_kind: "GRAPHIC_3D";
  color_details: IllustrationColorDetails | null;
  details: Graphic3DStyleDetails | null;
};
export type IconStyleModel = BaseStyleModel & {
  images_kind: "ICON";
  color_details: IllustrationColorDetails | null;
  details: IconStyleDetails | null;
};
type OtherStyleModel = BaseStyleModel & {
  images_kind: "OTHER";
};

type UndefinedStyleModel = BaseStyleModel & {
  images_kind: null;
};

export type StyleModel =
  | PhotographyStyleModel
  | IllustrationStyleModel
  | IconStyleModel
  | Graphic3DStyleModel
  | OtherStyleModel
  | UndefinedStyleModel;

export type ModelWithContents = ObjectModel | CharacterModel | StyleModel;

// FIXME: We should merge this with the Content type
export type InspirationContent = {
  resource_type: ResourceType;
  uuid: string;
  url: string;
  height: number | null;
  width: number | null;
  prompt: string;
  generation_ratio: GenerationRatio | null;
  reference_uuids: string[];
  created_at: string;
};

export const BOARD_NAME_PLACEHOLDER = "Untitled project";
